// @flow
import * as React from "react";
import PropTypes from 'prop-types';
import {reisy, use} from "reisy";
import {autorunAsync} from "mobx";
import {observer} from "mobx-react";

import type {Position} from "../scroller/utils.js";
import {scrollbarSize, isSafari, isEdge, isChrome} from "../environment.js";
import {chooseVariant} from "./image.js";
import {handleClick} from "./link.js";

import loadPagehandler from "../pagehandler/load.js";

import {
	needsFixedOrAnimatedBgHack,
	needsFixedBgForcedAcceleration,
	addFixedBgHackAttrToElement,
	addFixedBgHackForceAccelerationToElement,
	addScrollAnimatedBgHackAttrToElement,
	applyBgImageToElement,
	fixedRefViewport,
	projectFixedViewportIntoRect
} from "../pagehandler/utils/fixed-bg-helpers.js"

const kPageFixedBackgroundMaxImageWidth = 2600;

export const styles = reisy`
@namespace Page;
FlowNavigationSpacing: 60px;
enableSetFocus: true;
ytDisclaimer: ;
ignoreMaxWidth: false;
afterVideoFadeOut: ;
cancelPageAnchorRouting: false;
floorOuterPageSize: false;

Outer {
	position: relative;
	z-index: 1;
	flex-shrink: 0;
	background: white;
	background-size: cover;
	background-position: center center;
	font-size: 16px;
	min-width: 100%;
	overflow: hidden;
	direction: ltr;
}
OuterFlow {
	@extends Outer;
	max-width: 100%;
}
OuterSafariFix {
	will-change: transform;
}
OuterSafariWbrFix {
	& x-wbr:has(+ span:empty:last-child) {
		display: block;
	}
}

Inner {
	/*
	sometimes I hate css layout so much!
	we need this bogus rule here so that *inner* actually wraps its content.
	otherwise the contents vertical margins would be *outside* of *inner*
	*sigh*
	*/
	position: relative;
	transform-origin: 0 0;
	outline: none;
	background: inherit;
}
InnerFlow {
	@extends Inner;
	max-width: 800px;
	margin: auto;
	min-height: 100vh;
	padding-top: $FlowNavigationSpacing;
}

Fixed {
	@extends Helpers.Clip;
	position: absolute;
	width: 100%;
	height: 100%;
	top: 0;
	pointer-events: none;
	z-index: 0;
}

BGClip {
    & > * {
      position: absolute;
      top: 0;
      bottom: 0;
      left: 0;
      width: 1px;
      transform-origin: 0 0;
	  transform: translateZ(0px);
    }

    & > *:nth-child(2) {
      left: initial;
      right: 0;
      transform-origin: 100% 0;
    }
  }
`;

type Fit = "width" | "height" | "both";

// XXX: since this function is `.toString`-ed into the SSR page, we should
// not instrument it for the sake of stable snapshot rendering
// istanbul ignore next
function calculateSizes(elem: HTMLElement, fit: Fit,
	horizontal: boolean, size: [number, number], maxWidth?: number, zoom?: any = 1, stretch?: boolean = true, floor?: boolean = false) {
	let _scrollbarSize = 0;
	try {
		// Since this is a module import, babel transforms it into a property
		// access. And when this is then injected into the page for SSR, it will
		// lead to a script error -_-
		_scrollbarSize = scrollbarSize;
	} catch (e) {}

	// Long pages in horizontal mode get vertical scrollbars
	const scrollbarRight = (horizontal && fit === "width") ? _scrollbarSize : 0;
	// We got horizontal scrollbars in horizontal mode or for wide pages
	const scrollbarBottom = (horizontal || fit === "height") ? _scrollbarSize : 0;

	//Opera & Chrome in win10 with screen or browser scaling doesn't round the containerWidth when checking for scroll overflow. 
	//so we have to fix this ourselves
	const isOpera = window ? window.navigator.userAgent.indexOf('OPR/') > 0 : false;
	const isChrome = window ? /Chrome/.test(window.navigator.userAgent) && /Google Inc/.test(window.navigator.vendor) : false;
	const devicePixelRatio = window && window.devicePixelRatio || 1;
	const widthOfElem = elem => ((isOpera || isChrome) && devicePixelRatio !== 1) ? Math.floor(elem.getBoundingClientRect().width) : elem.clientWidth
 
	const availSize = [
		// The Page element, its immediate Scroller and the Reader Scroller.
		// $FlowFixMe: STFU, I know the parent is valid!
		(zoom > 1 ? widthOfElem(elem.parentNode.parentNode.parentNode) : widthOfElem(elem.parentNode.parentNode)) * zoom - scrollbarRight,
		// No matter the mode, the vertical height is always the window height.
		window.innerHeight * zoom - scrollbarBottom,
	];

	const ratios = [
		availSize[0] / size[0],
		availSize[1] / size[1],
	];
	let ratio =
		fit === "width" ? ratios[0] :
		fit === "height" ? ratios[1] :
		Math.min(ratios[0], ratios[1]);
	if (maxWidth) {
		ratio = Math.min(ratio, zoom * maxWidth / size[0]);
	}

	// size and position the inner page
	const scaledSize = floor ?  [
		Math.floor(size[0] * ratio - 1e-6),
		Math.floor(size[1] * ratio - 1e-6),
	] : [
		Math.ceil(size[0] * ratio - 1e-6),
		Math.ceil(size[1] * ratio - 1e-6),
		//Math.ceil(parseFloat((size[0] * ratio).toFixed(6))),
		//Math.ceil(parseFloat((size[1] * ratio).toFixed(6))),
	];
	const finalSize = [
		Math.max(availSize[0], scaledSize[0]),
		Math.max(stretch ? availSize[1] : 0, scaledSize[1]),
	];

	const offset = [
		0,
		0,
	];
	
	offset[0] = Math.floor((finalSize[0] - scaledSize[0]) / 2);
	
	if (fit !== "width") {
		offset[1] = Math.floor((finalSize[1] - scaledSize[1]) / 2);
	}

	const state = {
		size: finalSize,
		offset,
		scale: ratio,
		outerLeft: Math.floor(elem.parentElement.parentElement.getBoundingClientRect().left)
	};

	return state;
}

type Props = {
	index: number;
	position: Position;
	horizontal: boolean;
	onFormSubmit: () => void;
	onBeforeFocus: () => void;
	onAfterFocus: () => void;
	formatMessage: (msg: string) => string;
	// TODO:
	page: any;
	onScrollTrigger: any;
	scrollToStart: any;
};

const hasFocus = (props: Props) => props.position.page === props.index;

type Sizes = {
	size: [number, number];
	offset: [number, number];
	scale: number;
};

const FLOW_SIZES: Sizes = {
	size: [1024, 768],
	offset: [0, 0],
	scale: 1,
};

class Page extends React.Component {
	props: Props;
	static contextTypes = {
		player: PropTypes.object,
	};

	// TODO:
	handler: ?any = null;
	sizes: Sizes = FLOW_SIZES;

	dispose: () => void = () => {};
	handleResize = () => this.updateSize(this.props);

	pagestyle = React.createRef();
	inner = React.createRef();
	elem = React.createRef();
	fixed = React.createRef();

	handleLinkClick = (ev: MouseEvent) => {
		const r = new RegExp(`${this.props.story.id}(/|#).*?page=([0-9]+)`);
		const m = ev.currentTarget.href.match(r)

		if (m && m[2] === '1') {
			const {scrollToStart} = this.props;
			scrollToStart();
			ev.stopPropagation();
			ev.preventDefault();
		} else if(m && (styles.cancelPageAnchorRouting || this.props.story.options.cancelPageAnchorRouting)) {
			const {onScrollTrigger} = this.props;
			const elem = this.elem.current.parentElement.parentElement.parentElement.querySelector(`#page-${m[2]}`);
			elem && onScrollTrigger(elem, 0);
			ev.stopPropagation();
			ev.preventDefault();
		} else {
			handleClick({
				player: this.context.player,
			}, ev)
		}
	};

	constructor(props: Props) {
		super(props);
		this.state = {
			render: false
		};
		this.componentWillReceiveProps(this.props);

		if (this._needsFixedBgClipper()) {
			this._fixedBgLimitClipperRef = React.createRef();
		}
	}

	componentWillReceiveProps(props: Props) {
		if (props.page !== this.props.page) {
			this.unbindHandler();
		}
		this.updateSize(props);
		this.dispose();
		// XXX: So here is the deal:
		// We are calling `.focus()` as necessary inside the autorun function. But
		// focus actually changes the scroll offset natively via the browser. That
		// would completely mess up the scroll offset handling of the Scroller,
		// which is supposed to take care of that. So just run this function async,
		// so that the Scroller will already have set the new scroll offset so that
		// the native browser behavior does not mess with it anymore *sigh*.
		this.dispose = autorunAsync(`Page{${props.index}}.checkFocusAndPreload`, () =>
			this.checkFocusAndPreload(props), 0);
	}

	componentDidMount() {
		if (!IS_BROWSER) { return; }
		this.updateSize(this.props);
		this.componentDidUpdate();
		window.addEventListener("resize", this.handleResize);

		if (needsFixedOrAnimatedBgHack() && this.elem.current && this.props.page.background) {
			let needsHack = false;

			if (this.props.page.background.fixed) {
				addFixedBgHackAttrToElement(this.elem.current);
				needsHack = true;
			} else if (this.props.page.background.scrollScale) {
				addScrollAnimatedBgHackAttrToElement(this.elem.current);
				needsHack = true;
			}

			if (needsHack && needsFixedBgForcedAcceleration()) {
				addFixedBgHackForceAccelerationToElement(this.elem.current);
			}
		}
	}

	componentDidUpdate() {
		loadPagehandler().then(_handler => this.maybeStartHandler(_handler.default));
	}

	maybeStartHandler(Handler) {
		const {page, onFormSubmit, onScrollTrigger, onBeforeFocus, onAfterFocus, formatMessage, index, story} = this.props;
		const isFlow = page.type === "flow";
		const {scale} = this.sizes;
		const {inner, fixed} = this;
		if ((!isFlow && !scale) || !inner.current || !fixed.current || !page.loaded || this.handler) { return; }

		const options = {
			index,
			onFormSubmit,
			onBeforeFocus,
			onAfterFocus,
			onLinkClick: this.handleLinkClick,
			scrollTo: onScrollTrigger,
			scrollToTop: () => onScrollTrigger(inner.current, 0),
			formatMessage,
			player: this.context.player,
			enableSetFocus: styles.enableSetFocus,
			updatePageSize: () => {
				this.updateSize(this.props);
				this.props.onSizeChange && this.props.onSizeChange();
				this.setState({ render: this.state.render });
			},
			onPageScrolled: page.background && page.background.image
				? (rect, pct) => {
					if (!page.background.scrollScale) {
						// just for visibility tracking.
						this._updatePageBackgroundImage(false);
						return;
					}

					const effectivePct = page.background.scrollScale >= 0 ? pct : 1 - pct;

					this._updatePageBackgroundImage(
						false,
						effectivePct * Math.abs(page.background.scrollScale)
					);
				} : undefined,
			video: { youtube: { disclaimer: styles.ytDisclaimer }, afterFadeOut: styles.afterVideoFadeOut },
			hasWebP: !!story.options.hasWebP,
			hasAVIF: !!story.options.hasAVIF
		};
		this.handler = new Handler(inner.current, fixed.current, page, this.sizes, hasFocus(this.props), options);
		this._updatePageBackgroundImage(true);
	}

	componentWillUnmount() {
		window.removeEventListener("resize", this.handleResize);
		this.dispose();
		this.unbindHandler();
	}

	checkFocusAndPreload(props: Props) {
		const {page, horizontal} = props;
		const {handler} = this;
		if (handler) {
			handler.setFocus(hasFocus(props));
			// trigger scroll twice here, because of trigger system internals :-(
			handler.onScroll();
			handler.onScroll();
		}

		const isPrinting = IS_BROWSER && window.matchMedia('print').matches;

		const doPreload = IS_BROWSER && (
			Math.abs(props.position.page - props.index) <= 2 ||
			(!horizontal && page.fit ==='width')) || isPrinting;
		
		if (doPreload && !this.state.render) {
			// XXX: move that stuff to mobx?
			page.load(props.stylePrefix).then(() => this.setState({render: true}));
		}
	}

	updateSize(props: Props) {
		const {page, horizontal, stretch, index} = props;
		const {type, fit, maxWidth} = page;
		let {size: pageSize} = page;
		const customSize = this.handler && this.handler.customPageSize || null;
		if (type === "flow") {
			this.sizes = FLOW_SIZES;
			if (this.handler) {
				this.handler.updateSizing();
			}
			return;
		}
		const {elem, inner, pagestyle} = this;
		if (!elem.current) { return; }

		pageSize = customSize || pageSize;

		const zoom = props.zoom || 1;
		const sizes = this.sizes = calculateSizes(elem.current, fit, horizontal, pageSize, styles.ignoreMaxWidth ? undefined : maxWidth, zoom, stretch, styles.floorOuterPageSize);

		let {size, scale, offset} = sizes;
		let scaleFactor = 1;

		if ((isSafari || isChrome || isEdge) && window.innerWidth > window.screen.width) {
			const frag = scale / 1.5;
				
			if (frag > 1) {
				scaleFactor = Math.floor(scale) * 1.5;
				scale = scale / scaleFactor;
			}
		}
		
		inner.current.style.width = `${pageSize[0] * scaleFactor}px`;
		inner.current.style.height = `${pageSize[1] * scaleFactor}px`;
		inner.current.style.fontSize = `${scaleFactor * 100}%`;
		inner.current.setAttribute("data-scale-factor", scaleFactor);

		inner.current.style.transform = `translate(${offset[0]}px, ${offset[1]}px) scale(${scale})`;

		const curExtraScale = this._currentSizingInfo ? this._currentSizingInfo.extraScale : undefined;
		const curImgSize = this._currentSizingInfo ? this._currentSizingInfo.imgSize : undefined;

		this._currentSizingInfo = {
			effectiveFit: fit,
			pageSize,
			size: sizes.size,
			scale: sizes.scale,
			containerLeft: sizes.outerLeft,
			extraScale:
			undefined !== curExtraScale ? curExtraScale : (
				page.background.scrollScale < 0
					? Math.abs(page.background.scrollScale)
					: 0
				),
			imgSize: undefined !== curImgSize ? curImgSize : (
				page.background && page.background.image
					? chooseVariant(page.background.image, { width: 1}).size
					: undefined
			)
		};

		this._updatePageBackgroundImage(true);

		elem.current.style.width = `${size[0]}px`;
		elem.current.style.height = `${size[1]}px`;

		if (IS_BROWSER) {
			const A4Width = (isSafari ? 190 : 210) * 3.78; //190mm in css pixels
			const A4Height = (isSafari ? 277 : 297) * 3.78; //277mm in css pixels

			//some additional print stylesheets
			pagestyle.current.innerHTML = `
				@media print {
					@page {
						margin: ${(isSafari ? 10 : 0) * 3.78}px;
						size: A4;
					}

					body {
						-webkit-print-color-adjust: exact;
					}

					#page-${index} .${styles.Outer} {
						width: ${A4Width}px !important;
						height: ${A4Width * size[1] / (size[0] - offset[0] * 2)}px !important;
						min-width: ${A4Width}px !important;
						min-height: ${A4Height}px !important;
					}
					#page-${index} .${styles.Inner} {
						transform: translate(0, 0) scale(1) !important;
						font-size: ${16 * A4Width / pageSize[0]}px !important;
						width: ${A4Width}px !important;
						height: ${A4Width * pageSize[1] / pageSize[0]}px !important;
					}
					#page-${index} .${styles.Inner} > *, .${styles.Inner} > a > * {
						opacity: 1 !important;
						visibility: visible !important;
					}
					#page-${index} .${styles.Inner} div.IKBShape {
						display: flex;
						flex-direction: column;
						justify-content: flex-start;
						clear: both;
					}
					#page-${index} .${styles.Inner} p, .${styles.Inner} h2 {
						clear: both;
					}
				}
			`;
		}

		if (IS_BROWSER && zoom > 1) {
			/*const parent = this.props.scrollParent;
			setTimeout(() => {
				parent.currentScroll = (parent.elem.scrollWidth - parent.elem.offsetWidth) * 0.5
			}, 1);*/
			setTimeout(() => {
				window.scrollTo((document.body.scrollWidth - window.innerWidth) * 0.5, window.scrollTop);
			}, 1);
		}

		if (this.handler) {
			this.handler.updateSizing(sizes);
		}
	}

	_needsFixedBgClipper() {
		return this.props.page.background.fixed && this.props.page.background.limitWidth;
	}

	_updatePageBackgroundImage(isResize = false, bgExtraScale?: number) {
		const bg = this.props.page.background;

		if (!bg.image || !this._currentSizingInfo || !this.handler || !this.elem.current || !this.inner.current) {
			return;
		}

		this._currentSizingInfo.extraScale = undefined !== bgExtraScale
			? bgExtraScale
			: this._currentSizingInfo.extraScale;
		
		const {
			effectiveFit,
			pageSize,
			imgSize,
			containerLeft,
			size,
			scale,
			extraScale: extraBgScale,
		} = this._currentSizingInfo;

		const { elem, inner } = this;

		let bgScale = (bg.size > 0 ? bg.size : 1) + (extraBgScale || 0);
		let pos = bg.pos;
		let imagePixelWidth = size[0];

		if (bg.fixed) {
			const vp = fixedRefViewport();
			const limitWidth = bg.limitWidth
        		? Math.min(pageSize[0], size[0])
        		: size[0];
      		const limitInset = bg.limitWidth
        		? Math.max(0, (size[0] - pageSize[0]) / 2)
        		: 0;
      		const offLeft = containerLeft + limitInset;
		
			const proj = projectFixedViewportIntoRect(
				vp,
				{ left: offLeft, top: 0, width: limitWidth, height: vp.maxHeight },
				imgSize,
				bg.size,
				bg.pos,
				extraBgScale
			);
		
			bgScale = proj.scale;
			pos = proj.position;
			imagePixelWidth = limitWidth;

			if (
				isResize &&
				this._needsFixedBgClipper() &&
				this._fixedBgLimitClipperRef &&
				this._fixedBgLimitClipperRef.current
			  ) {
				const n = this._fixedBgLimitClipperRef.current.children;
		
				for (let i = 0; i < n.length; i++) {
					const el = n.item(i);
					el.style.backgroundColor = bg.color || "#fff";
					el.style.transform = `scale(${limitInset.toFixed(2)}, 1)`;
				}
			  }
		} else if ((bg.size <= 0 || !bg.size) && (effectiveFit === "both" || effectiveFit === "width")) {
			const cas = size[0] / size[1],
			  ias = imgSize[0] / imgSize[1];
	  
			bgScale = Math.max(1, ias / cas) + (extraBgScale || 0);
		}

		const targetElem = bg.limitWidth && !bg.fixed ? inner : elem;
	
		// set "fixed" attachment early, so that the browser doesn't need
    	// to rebuild its rendering tree later during interactivity.
		if (bg.fixed) {
			targetElem.current.style.backgroundAttachment = "fixed";
			inner.current.style.background = "none";
		}

		//if the the page is not visible in the viewport skip background calculations
		const y = this.handler.layer.getGlobalMatrix()[13];
		const threshold = 400;
		if (y > window.innerHeight + threshold || y < -size[1] - threshold) {
			return;
		}

		const bgVariant = chooseVariant(
			bg.image,
			{
				pickClosest: bg.fixed,
				// in kent-next, chooseVariant multiplies the devicePixelRatio in by itself,
				// so we need to account for that here.
				width: Math.min(
					bg.fixed ? kPageFixedBackgroundMaxImageWidth / (Math.max(1, devicePixelRatio || 1)) : 10000,
					imagePixelWidth * ((bg.size > 0 ? Math.min(1, bg.size) : 1) + Math.abs(bg.scrollScale || 0))
		 		)
			}
		);

		applyBgImageToElement(targetElem.current, bgVariant.url, bg.fixed, !!bg.scrollScale);

		if (pos) {
			targetElem.current.style.backgroundPosition = `${(pos[0] * 100).toFixed(4)}% ${(
				pos[1] * 100
			).toFixed(4)}%`;
		}

		if (bg.size) {
			targetElem.current.style.backgroundSize = `${(bgScale * 100).toFixed(4)}%`;
		}

		if (bg.repeat) {
			targetElem.current.style.backgroundRepeat = bg.repeat;
		}

		// since we support blend modes for content elements, we use
		// background: inherit; on the actual page, in order to have the xbg
		// available for blending. However, since the page can be scaled with
		// respect to the outer container, we need to position a background
		// image, so that it blends directly with the full-size background on the
		// container.
		if (bgVariant.size && !bg.fixed && !bg.limitWidth) {
			const scaledPageSize = [
				pageSize[0] * scale,
				pageSize[1] * scale,
			];

			if (pos && (!!bg.size || bg.size === 0)) {
				const s0 = size[0], s1 = size[1];
				const bs = bgScale;

				// logical size of visible image, e.g. image size scaled by width
				const is0 = s0 * bs,
					is1 = s0 * bs * (imgSize[1] / imgSize[0]);

				// size scale for the background image.
				const ibs = bs * (s0 / scaledPageSize[0]);

				// outer element background position, in pixels
				const po0 = (s0 - is0) * pos[0],
					po1 = (s1 - is1) * pos[1];

				const refSize = s0 > is0 ? size : scaledPageSize;

				// inner element background position, in pixels, by offsetting
				// according to the overlap position.
				const pi0 = (po0 - (s0 - refSize[0]) / 2) / scale;
				const pi1 = (po1 - (s1 - refSize[1]) / 2) / scale;

				// calculate %-based image position for inner element from the pixel
				// values, so that we get better sub-pixel rendering by the browser
				const ppi0 = pi0 / (pageSize[0] - is0 / scale);
				const ppi1 = pi1 / (pageSize[1] - is1 / scale);

				inner.current.style.backgroundSize = `${(ibs * 100).toFixed(8)}%`;
				inner.current.style.backgroundPosition = `${(ppi0 * 100).toFixed(8)}% ${(
					ppi1 * 100
				).toFixed(8)}%`;
			} else {
				// we use background-size: cover; on the outer page, so we need to
				// figure out which side "covers".
				const coverSizeIdx =
					size[0] / bgVariant.size[0] <
					size[1] / bgVariant.size[1]
						? 1 // covers y dimension
						: 0; // covers x dimension;
				
				const scaledPageSize = [
					pageSize[0] * scale,
					pageSize[1] * scale,
				];

				const bgScale =
					(size[coverSizeIdx] / scaledPageSize[coverSizeIdx]) +
					(extraBgScale || 0);

				inner.current.style.backgroundSize = `${(bgScale * 100).toFixed(8)}% ${(
					bgScale * 100
				).toFixed(8)}%`;
		
				let bgPos = "50% 50%";

				switch (effectiveFit) {
					case "width": {
						if (coverSizeIdx == 0 /* intentionally not === */) {
							const pixelsY = (size[1] - imgSize[1] * bgScale) / 2;
							const scaledBgHeight = imgSize[1] * bgScale;
							const pct =
								scaledBgHeight !== scaledPageSize[1]
								? -pixelsY / (scaledBgHeight - scaledPageSize[1])
								: 0.5;
							bgPos = `50% ${(pct * 100).toFixed(8)}%`;
						} else {
							bgPos = "50% 0";
						}
						break;
					}
			
					default:
						break;
				}
		
				inner.current.style.backgroundPosition = bgPos;
			}
		}
	}

	unbindHandler() {
		if (!this.handler) { return; }
		this.handler.destroy();
		this.handler = null;
	}

	render() {
		const {page, horizontal, stretch = true} = this.props;

		const isFlow = page.type === "flow";
		const outer: any = {
			backgroundColor: page.background.color,
		};
		let inner;
		if (!isFlow) {
			const pageSize = this.handler && this.handler.customPageSize || page.size;
			inner = {
				width: `${pageSize[0]}px`,
				height: `${pageSize[1]}px`,
			};
		}

		const innerHTMLProp = {};
		if (!IS_BROWSER) {
			// when prerendering, we can just inject some script that runs *before*
			// the player is initialized to fix up the page scaling!
			// damn how low did we sink with all the workarounds… -_-
			let html = `<style>
			.hide-next-sibling + * { opacity: 0; }
			.editorPage { background: transparent !important; }
			</style>
			${page.processHTML(this.context.player.url)}
			<script>
			var calculateSizes = ${calculateSizes.toString()};
			var el = document.querySelector(".editorPage");
			if (el) {
				el = el.parentNode.parentNode;
				var sizes = calculateSizes(el.parentNode, ${JSON.stringify(page.fit)}, ${JSON.stringify(horizontal)}, ${JSON.stringify(page.size)}, ${JSON.stringify(styles.ignoreMaxWidth ? 0 : page.maxWidth)}, ${JSON.stringify(stretch)}, ${JSON.stringify(styles.floorOuterPageSize)});
				el.style.transform = "translate(" + sizes.offset[0] + "px, " + sizes.offset[1] + "px) scale(" + sizes.scale + ")";
				el.parentNode.style.width = sizes.size[0] + "px";
				el.parentNode.style.height = sizes.size[1] + "px";
			}
			</script>`;

			// and while we are at it why not hide all the animated elements?
			// this would be the fadeIn & fadeSlideIn elements
			// unless the user agent is a bot or we are displayed as a google cache -> major hack...
			html = html.replace(/data-fullscreen-captions="\[.*?\]" /gi, ''); //replace all captions to remove possible html content
			html = html.replace(/(<[^<]+?data-actions="[^"]*'named':'(fadeIn|fadeSlideIn)')/gi, '<script>if (!~navigator.userAgent.indexOf(\'bot\') && !~window.location.href.indexOf(\'webcache.googleusercontent.com\')) { document.write(\'<div class="hide-next-sibling"></div>\'); }</script>$1');

			innerHTMLProp.dangerouslySetInnerHTML = {__html: html};
		}

		return [
			<style ref={this.pagestyle} key="styles"></style>,
			<div ref={this.elem} {...use(
				isFlow ? styles.OuterFlow : styles.Outer,
				outer,
				isSafari ? styles.OuterSafariWbrFix : null,
				isSafari && this.props.horizontal ? styles.OuterSafariFix : null
			)} key="page">
				<div {...use(styles.Fixed)} ref={this.fixed} key="fixed" />
				{this._needsFixedBgClipper() ? (
					<div
						ref={this._fixedBgLimitClipperRef}
						{...use(styles.BGClip)}
						key="clip"
					>
						<div />
						<div />
					</div>
				) : null}
				<div
					key="content"
					ref={this.inner}
					tabIndex="0"
					{...use(isFlow ? styles.InnerFlow : styles.Inner, inner, "kent-page", isFlow ? "kent-page-flow" : null)}
					{...innerHTMLProp} />
			</div>
		];
	}
}

export default observer(Page);



// WEBPACK FOOTER //
// ./lib/components/page.js